/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {BehaviorSubject, EMPTY, Observable, Subject} from 'rxjs';
import {catchError, filter, map, shareReplay, switchMap, tap} from 'rxjs/operators';

import {SearchOptions} from './user.module';

import {Profile} from '../../+settings/user-rights/user-rights.interfaces';
import {REQUEST_OPTIONS, ResponseStatusCodes} from '../../app.constants';
import {
  CliCoreAttributes,
  CliEmpAttributes,
  CliProfileAttributes,
  CliUserAttributes, CliUserDTO,
  CliUserPreferences,
  CreateUserData, EmpCliUserDTO,
  SsoTypeEnum
} from '../../models/models.interfaces';
import {ApiListData, ApiService, ApiSingleData, Data, DataListAndCount, Link} from '../api';
import {AuthenticationEvent, AuthenticationService} from '../authentication';
import {AdminUser} from '../employees-list/list/employee-list.interfaces';
import {Employee} from '../employees/employees.model';

/**
 * User class : wraps user attributes and access to cliCore and cliProfile.
 */
export class User {

  // User id
  get id(): number {
    return this.data.id;
  }

  get cliEmpId(): number {
    return this.data.attributes.cliEmpID;
  }

  // User attributes shortcuts
  get login(): string {
    return this.data.attributes.login;
  }

  get firstName(): string {
    return this.data.attributes.firstName;
  }

  get lastName(): string {
    return this.data.attributes.lastName;
  }

  get title(): string {
    return this.data.attributes.title;
  }

  get email(): string {
    return this.data.attributes.email;
  }

  get ssoType(): SsoTypeEnum {
    return this.data.attributes.ssoType;
  }

  set email(value: string) {
    this.data.attributes.email = value;
  }

  get timezone(): string {
    return this.data.attributes.timezone;
  }

  set timezone(value: string) {
    this.data.attributes.timezone = value;
  }

  get dateFormat(): string {
    return this.data.attributes.dateFormat;
  }

  set dateFormat(value: string) {
    this.data.attributes.dateFormat = value;
  }

  get integerMask() {
    return this.data.attributes.integerMask;
  }

  set integerMask(value: string) {
    this.data.attributes.integerMask = value;
  }

  get decimalMask() {
    return this.data.attributes.decimalMask;
  }

  set decimalMask(value: string) {
    this.data.attributes.decimalMask = value;
  }

  get autoSave() {
    return this.data.attributes.autoSave;
  }

  set autoSave(value: boolean) {
    this.data.attributes.autoSave = value;
  }

  get preferences(): CliUserPreferences {
    return this.data.attributes.preferences;
  }

  set preferences(preferences: CliUserPreferences) {
    this.data.attributes.preferences = preferences;
  }

  get sendEmail() {
    return this.data.attributes.sendEmail;
  }

  set sendEmail(value: boolean) {
    this.data.attributes.sendEmail = value;
  }

  get phoneNumber(): string {
    return this.data.attributes.phoneNumber;
  }

  get lastConnection(): string {
    return this.data.attributes.lastConnection;
  }

  get language(): string {
    return this.data.attributes.locale;
  }

  set language(value: string) {
    this.data.attributes.locale = value;
  }

  get notes(): string {
    return this.data.attributes.notes;
  }

  get isAdmin(): boolean {
    return this.data.attributes.isAdmin;
  }

  // Computed properties
  get fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get profilePicture(): string {
    const picture = this.data.links.find(link => link.rel === 'photo');

    if (!picture) {
      return;
    }

    return picture.href;
  }

  get companyName(): string {
    const cliCore = this.getCliCore();
    if (cliCore === undefined) {
      console.warn('cliCore or cliCore links not found for user', this);
      return undefined;
    }
    return cliCore.attributes.name;
  }

  get cliCoreId(): number {
    const cliCore = this.getCliCore();

    return cliCore ? cliCore.id : null;
  }

  constructor(public data: Data<CliUserAttributes>, private included: Array<Data<any>>) {
  }

  public getLink(rel: string): Link {
    return this.data.links.find(link => link.rel === rel);
  }

  public getCliCore(): Data<CliCoreAttributes> {
    return this.included.find(data => data.type === 'cliCore');
  }

  public getCliProfile(): Data<CliProfileAttributes> {
    return this.included.find(data => data.type === 'cliProfile');
  }

  public updateAttrs(data) {
    Object.assign(this.data.attributes, data);
  }
}

const checkAndReturnUserResponse = (response: any) => {
  if (response.status === ResponseStatusCodes.OK) {
    const apiResponse = <ApiSingleData<CliUserAttributes>>response.body;
    const user = new User(apiResponse.data, apiResponse.included);
    try {
      window['DD_RUM'].setUser({
        id: '' + user.id
      });
    } catch (error) { /* empty */
    }
    return user;
  }
  console.error(`Failed to fetch user. Status : ${response.status}`);
  return <User>undefined;
};

/**
 * Authentication services that logs in and logs out users against the server.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {

  public userSubject: BehaviorSubject<User> = new BehaviorSubject(null);
  public newAdminUserCandidate$: Subject<AdminUser> = new Subject();
  public resetEmployees$: Subject<boolean> = new Subject();

  public createdUser = new Subject<Employee>();
  private userObservable = this.userSubject.asObservable().pipe(filter((user: User) => user !== null));
  private cacheUser = {};

  constructor(private authenticationService: AuthenticationService,
              private api: ApiService) {

    this.authenticationService.authenticationEvents.pipe(
      switchMap((result => {
        if (result.event === AuthenticationEvent.TOKEN_VERIFIED && result.response) {
          this.userSubject.next(checkAndReturnUserResponse(result.response));
          return EMPTY;
        } else if (result.event === AuthenticationEvent.LOGIN_SUCCESSFUL) {
          return this.fetchUser();
        } else if (result.event === AuthenticationEvent.LOGOUT) {
          this.userSubject.next(null);
          return EMPTY;
        }
      }))).subscribe();
  }

  /**
   * Gets the currently authenticated user.
   *
   * @returns {Observable<User>}
   */
  public getUser(): Observable<User> {
    return this.userObservable;
  }

  /**
   * Is current user admin ?
   *
   * @returns {Observable<boolean>}
   */
  public isAdmin(): Observable<boolean> {
    return this.getUser().pipe(map(user => user.isAdmin));
  }

  /**
   * Partial update of user data
   *
   * @param userData
   * @returns {Observable<Response>}
   */
  public updateUserData(userData: { id: number; [key: string]: any }) {
    return this.api.post(`/authent/api/v1/cliUser/${userData.id}`, userData);
  }

  /**
   * Create user for employee
   *
   * @param userData
   * @returns {Observable<Response>}
   */
  public createUser(userData: CreateUserData) {
    return this.api.post(`/authent/api/v1/cliUser/upsertCliUser`, userData);
  }

  /**
   * Activate/deactivate user for employee
   *
   * @param activateData
   * @returns {Observable<Response>}
   */
  public activateUser(activateData: { activate: boolean; cliEmpIds: number[] }) {
    return this.api.post(`/authent/api/v1/cliUser/activation`, activateData);
  }

  /**
   * Activate/deactivate user by id
   *
   * @param activateData
   * @returns {Observable<Response>}
   */
  public activateOrDeactivateUser(userId: number, activate: boolean) {
    return this.api.post(`/authent/api/v1/cliUser/activation/${userId}?active=${activate}`);
  }

  public deleteUser(userId: number): Observable<User> {
    return this.api.delete(`/authent/api/v1/cliUser/` + userId);
  }

  public getKnowledgeUri(): Observable<string> {
    return this.api.silent.get(`/ga/api/v1/client/knowledge-uri`, REQUEST_OPTIONS.RESPONSE_TYPE.TEXT);
  }

  public getUserById(userId: number): Observable<User> {
    if (!this.cacheUser[userId]) {
      this.cacheUser[userId] = this.requestUser(userId);
    }
    return this.cacheUser[userId];
  }

  private requestUser(userId: number): Observable<User> {
    return this.api.get(`/authent/api/v1/cliUser/${userId}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(checkAndReturnUserResponse),
        shareReplay(1)
      );
  }

  public getAllNonEmployeeUsers(options: SearchOptions = {}, fixedFilter?: string): Observable<DataListAndCount<CliEmpAttributes>> {
    const queryString = this.getFiqlQueryStringForCliUsers(options, fixedFilter);
    return this.api.authent.get(`/cliUser/non-emp-cli-users?${queryString}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => {
          return {
            items: result.status === 204 ? {data: []} : this.transform(result.body),
            total: Number(result.headers.get('x-total-count')) || 0
          };
        })
      );
  }

  public getAllEmployeeUsers(options: SearchOptions = {}, fixedFilter?: string): Observable<DataListAndCount<CliEmpAttributes>> {
    const queryString = this.getFiqlQueryStringForCliUsers(options, fixedFilter);
    return this.api.authent.get(`/cliUser/emp-cli-users?${queryString}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => {
          return {
            items: result.status === 204 ? {data: []} : this.transformUserInEmployee(result.body),
            total: Number(result.headers.get('x-total-count')) || 0
          };
        })
      );
  }

  public getAllCliUsers(options: SearchOptions = {}): Observable<DataListAndCount<CliEmpAttributes>> {
    const queryString = this.getFiqlQueryStringForCliUsers(options);
    return this.api.authent.get(`/cliUser/cli-users?${queryString}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => {
          return {
            items: result.status === 204 ? {data: []} : this.transform(result.body),
            total: Number(result.headers.get('x-total-count')) || 0
          };
        })
      );
  }

  public getAllEmpCliUsers(searchTerm: string): Observable<DataListAndCount<EmpCliUserDTO[]>> {
    const splitAndBuildQuery = searchTerm.trim().split(' ')
      .filter(c => c.length)
      .map(c => c.replace(/\W+/g, ''))
      .map(c => 'fullName==*' + encodeURIComponent(c) + '*')
      .join(',');
    const search = searchTerm?.replace(' ', '').length ? splitAndBuildQuery : '';
    return this.api.authent.silent.get(`/cliUser/all-emp-cli-users?search=${search}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(result => {
          return {
            items: result.status === 204 ? {data: []} : {data: result.body},
            total: Number(result.headers.get('x-total-count')) || 0
          };
        })
      );
  }

  public getNonEmpUserIdByEmail(email: string): Observable<number> {
    const sanitizedEmail = encodeURIComponent(email);
    return this.api.authent.get(`/cliUser/non-emp-cli-users?search=email==${sanitizedEmail}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => response?.body[0]?.id)
      );
  }

  public getUsersByCliEmp(cliEmpId: number): Observable<Response> {
    return this.api.get(`/authent/api/v1/cliUser/getByCliEmpIds?cliEmpIds=${cliEmpId}`, REQUEST_OPTIONS.OBSERVE.RESPONSE);
  }

  public updateCreatedAdminUser(data) {
    this.newAdminUserCandidate$.next(data);
  }

  public resetEmployees() {
    this.resetEmployees$.next(true);
  }

  public fetchUser(): Observable<User> {
    return this.api.get('/authent/api/v1/cliUser/me', REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(checkAndReturnUserResponse),
        tap(user => this.userSubject.next(user)),
        catchError(() => {
          this.userSubject.next(null);
          return EMPTY;
        }));
  }

  public updateCliUserPreferences(cliUserPreferences: CliUserPreferences): Observable<CliUserPreferences> {
    return this.api.authent.post('/cliUser/update-preferences', cliUserPreferences);
  }

  private transformUserInEmployee(users: CliUserAttributes[]): ApiListData<CliEmpAttributes> {
    return {
      data: users.map(user => ({
        id: user.id,
        cliEmpId: user?.cliEmpId,
        attributes: {
          firstName: user.firstName,
          lastName: user.lastName,
          intName: '',
          intFirstName: '',
          intLastName: '',
          id: user.id,
          lastUpdateDate: '',
          cliCoreID: user.cliCoreId,
          contract: {},
          empContracts: [],
          page: user.page,
          userActive: user.active,
          userEmail: user.email,
          userLogin: user.login
        },
        links: []
      }))
    };
  }
  private transform(users: CliUserDTO[]): ApiListData<CliEmpAttributes> {
    return {
      data: users.map(user => ({
        id: user.id,
        cliEmpId: user?.cliEmpId,
        attributes: {
          firstName: user.firstName,
          lastName: user.lastName,
          intName: '',
          intFirstName: '',
          intLastName: '',
          id: user.id,
          lastUpdateDate: '',
          cliCoreID: user.cliCoreId,
          contract: {},
          empContracts: [],
          userActive: user.active,
          userEmail: user.email,
          userLogin: user.login
        },

        links: []
      }))
    };
  }

  private getFiqlQueryStringForCliUsers(options: SearchOptions, fixedFilter?: string) {
    const parts = Object.keys(options)
      .filter(key => options[key] !== undefined)
      .map(key => {
        if (key === 'search') {
          const searchFilter = isNaN(+options[key]) || options[key] === ''
            ? `(fullName=="*${options[key]}*")`
            : `(fullName=="*${options[key]}*" or cliEmpID=="${options[key]}" or id=="${options[key]}")`;
          const combinedSearch = fixedFilter ? `${fixedFilter};${searchFilter}` : searchFilter;
          return `search=${combinedSearch}`;
        } else if (key === 'orderBy') {
          return `order-by=${options[key]}`;
        } else if (key === 'perPage') {
          return `per-page=${options[key]}`;
        } else {
          return `${key}=${options[key]}`;
        }
      });

    if (fixedFilter && !options.search) {
      parts.unshift(`search=${fixedFilter}`);
    }

    return parts.join('&');
  }

  public getProfiles(): Observable<Profile[]> {
    return this.api.authent.get(`/userRights/profiles`);
  }
}
